import {
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Button as ButtonAnt,
  Modal,
  Typography,
  App
} from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import { Table } from '@/components/tables/table';
import { css } from '@emotion/react';
import FOOD_NUTRIENTS from '@/constants/food-nutrients.constant';
import { useCallback, useEffect, useState } from 'react';
import { FoodManagementNutrientDetails } from '@/components/forms/food-management/nutrients.form';
import { FoodManagementSearcher } from '@/components/forms/food-management/searcher.form';
import { useModal } from '@/hooks/modal.hook';
import { FoodManagementInformationModal } from '@/components/modals/food-management/information.modal';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { setMessages } from '@/redux/slices/app.slice';

import {
  getListFood,
  deleteFood,
  watchFoodPagination,
  watchListFood,
  resetMessages,
  watchFoodMessage
} from '@/redux/slices/food.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import dayjs from 'dayjs';
import { useQuery } from '@/hooks/query.hook';
import { PermissionMode } from '@/enums/mode.enum';
import { PageURLs } from '@/utils/navigate';
import { formatDateToHHmmDDMMYYYY } from '@/utils/common';
import usePermission from '@/hooks/permission.hook';

type PageChangeParams = {
  offset?: number;
  pageSize?: number;
  filter?: FilterFood;
};

export default function FoodSingleManagementTable() {
  const { modal } = App.useApp();
  const [selected, setSelected] = useState(undefined);
  const { openModal, closeModal } = useModal();
  const [filter, setFilter] = useState<FilterFood>({});
  const pagination = useAppSelector(watchFoodPagination);
  const dispatch = useAppDispatch();
  const listFood = useAppSelector(watchListFood);
  const message = useAppSelector(watchFoodMessage);
  const { push } = useQuery();
  const { updatable, deletable } = usePermission();

  const handlePageChange = useCallback(
    ({
      offset = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      const query = {
        name: filter?.name || '',
        materialGroup: filter?.materialGroup || [],
        groupFood: filter?.groupFood || "",
        startUpdateDate: filter?.startUpdateDate ? dayjs(filter.startUpdateDate).format('YYYY-MM-DD') : '',
        endUpdateDate: filter?.endUpdateDate ? dayjs(filter.endUpdateDate).format('YYYY-MM-DD') : '',
        latestChangeOnTop: filter?.latestChangeOnTop || false,
        searchKeys: '',

      };
      dispatch(getListFood({ query, offset, limit: pageSize }));
    },
    [dispatch]
  );

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  const handleSearch = (values: FilterDish) => {
    const isReset = Object.keys(values).length === 0;
    const newFilter = isReset ? {} : { ...filter, ...values };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleViewInformation = (food: any) => {
    openModal(
      <FoodManagementInformationModal
        food={food}
        onCancel={closeModal}
      />, { width: 1200 }
    );
  };

  const handleEditInformation = (food: any) => {
    push(
      `${PageURLs.ofFoodManagement()}?mode=${PermissionMode.EDIT}&foodId=${food.ID}`
    );
  };

  const handleDeleteFood = async (foodId: string) => {
    await dispatch(deleteFood(foodId));
    const remainingRecords = listFood.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        offset: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        offset: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
    dispatch(resetMessages());
  };

  useEffect(() => {
    if (message) {
      dispatch(
        setMessages([
          {
            type: message.type,
            message: message.message,
            description: message.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, message]);

  const confirmDeleteFood = (foodId: string) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá món ăn này?',
      content: (
        <em>
          Dữ liệu không thể khôi phục
        </em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDeleteFood(foodId)
    });
  };

  const nutrientColumns: TableColumnsType<any> = Object.keys(
    FOOD_NUTRIENTS
  ).map((key, _) => ({
    title: FOOD_NUTRIENTS[key].title,
    dataIndex: key,
    key,
    width: 'auto',
    render: (_, record) =>
      record[FOOD_NUTRIENTS[key]?.name] &&
      record[FOOD_NUTRIENTS[key]?.name] + FOOD_NUTRIENTS[key]?.unit
  }));

  const columns: TableColumnsType<any> = [
    {
      title: 'STT',
      render: (_, __, index) => (pagination.curPage - 1) * pagination.limitPage + index + 1,
    },
    {
      title: 'ID thực phẩm',
      dataIndex: 'ID',
      key: 'ID'
    },
    {
      title: 'Tên thực phẩm',
      dataIndex: 'NAME',
      key: 'NAME',
      width: 200,
      fixed: 'left'
    },

    {
      title: 'Hình Thumbnail',
      dataIndex: 'IMAGE',
      key: 'IMAGE',
      render: (record) => (
        <img
          src={record ? `${record}` : IMAGE_DEFAULT}
          alt='Food Thumbnail'
          style={{ width: '50px', height: '32px', objectFit: 'cover' }}
        />
      )
    },
    {
      title: 'Phân loại thực phẩm theo nhóm nguyên liệu',
      dataIndex: 'MATERIAL_NAME',
      key: 'MATERIAL_NAME',
      width: 200
    },
    {
      title: 'Phân loại thực phẩm theo nhóm dinh dưỡng',
      dataIndex: 'FOOD_GROUP_NAME',
      key: 'FOOD_GROUP_NAME',
      width: 200
    },
    {
      title: 'Khối lượng sống sạch',
      dataIndex: 'STANDARD_VOLUME',
      key: 'STANDARD_VOLUME',
      width: 200,
      render: (value) => `${value}g`
    },
    ...(nutrientColumns as TableColumnsType<FoodNutrient>),
    {
      title: 'Cập nhật mới nhất',
      dataIndex: 'MODIFIED_DATE',
      key: 'MODIFIED_DATE',
      width: 150,
      render: (_, record) => `${formatDateToHHmmDDMMYYYY(record.MODIFIED_DATE)}`,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CREATED_DATE',
      key: 'CREATED_DATE',
      width: 150,
      render: (_, record) => `${formatDateToHHmmDDMMYYYY(record.CREATED_DATE)}`,
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 104,
      render: (_, record) => (
        <Row gutter={4} wrap={false}>
          <Col>
            <Tooltip title='Xem thông tin'>
              <ButtonAnt
                onClick={() => handleViewInformation(record)}
                type='primary'
                icon={<EyeOutlined />}
                css={viewButtonStyles}
              />
            </Tooltip>
          </Col>
          {updatable && (
            <Col>
              <Tooltip title='Cập nhật'>
                <ButtonAnt
                  onClick={() => handleEditInformation(record)}
                  type='primary'
                  icon={<EditOutlined />}
                  css={editButtonStyles}
                />
              </Tooltip>
            </Col>
          )}
          {deletable && (
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDeleteFood(record.ID)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      )
    }
  ];
  return (
    <div css={rootStyles}>
      <FoodManagementSearcher onSearch={handleSearch} />

      <div css={boxStyles}>
        <Typography.Title level={5}>Danh sách thực phẩm</Typography.Title>

        <Table<any>
          columns={columns}
          dataSource={listFood}
          pagination={{
            showQuickJumper: false,
            showSizeChanger: true,
            current: pagination.curPage,
            pageSize: pagination.limitPage,
            total: pagination.totalRows,
            onChange: (offset, pageSize) => {
              handlePageChange({ offset, pageSize, filter });
            }
          }}
        />

        <Modal
          open={selected}
          onCancel={(e) => {
            e.stopPropagation();
            setSelected(undefined);
          }}
          width={1000}
        >
          <FoodManagementNutrientDetails food={selected} />
        </Modal>
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const viewButtonStyles = css`
  background-color: var(--green-dark-color) !important;
  color: var(--white-color);
`;

const editButtonStyles = css`
  background-color: var(--yellow-light-color) !important;
  color: var(--white-color);
`;